import React, { useState, useEffect } from 'react';
import { TableRow, TableCell, Button, IconButton, Collapse, Box, Typography, TableContainer, Table, TableBody, TablePagination } from '@mui/material';
import { KeyboardArrowDown as KeyboardArrowDownIcon, KeyboardArrowUp as KeyboardArrowUpIcon, KeyboardArrowRight } from '@mui/icons-material';
import { Clusters, Issues, SimilarCompanyIssue } from '../../../services/minute';
import IssuesTable from './IssuesTable';

interface RowProps {
  row: Clusters;
  index: number;
}

const ClustersTable: React.FC<RowProps> = ({ row, index }) => {
  const [open, setOpen] = useState(index === 0);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState('issue_score');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [showSimilarCompanyIssues, setShowSimilarCompanyIssues] = useState(false);

  useEffect(() => {
    setOpen(index === 0); // 検索時に最初のクラスターをデフォルトで展開
  }, [index]);

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const descendingComparator = (a: any, b: any, orderBy: string) => {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  };

  const getComparator = (order: 'asc' | 'desc', orderBy: string) => {
    return order === 'desc'
      ? (a: any, b: any) => descendingComparator(a, b, orderBy)
      : (a: any, b: any) => -descendingComparator(a, b, orderBy);
  };

  const sortedIssues = row.issues ? row.issues.slice().sort(getComparator(order, orderBy)) : [];

  const formatNumber = (num: string | number) => {
    const number = typeof num === 'string' ? parseFloat(num) : num;
    return number.toFixed(2);
  };

  const formatCompanyInfo = (company: SimilarCompanyIssue) => {
    const formattedScore = company.company_score.toFixed(2);
    if (parseFloat(formattedScore) < 0.70) return null;

    const truncatedIssueName = company.issue_name.length > 50 ? `${company.issue_name.slice(0, 50)}...` : company.issue_name;
    const docUrl = `https://disclosure2.edinet-fsa.go.jp/WZEK0040.aspx?${company.doc_id}`;
    return (
      <React.Fragment>
        <Typography>
          {company.id}. <strong>{company.JSIC}</strong>（{formattedScore}）<a href={docUrl} target="_blank" rel="noopener noreferrer">{company.company_name}</a>
        </Typography>
        <Typography variant="body2" component="div">
          {truncatedIssueName}
        </Typography>
        <Box mt={1}></Box>
      </React.Fragment>
    );
  };

  const formatIssueInfo = (issue: Issues) => {
    return (
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="body2" component="div">
          {issue.issue_name} <a href={issue.url} target="_blank" rel="noopener noreferrer">参考記事</a>
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          size="small"
          onClick={() => handleCauseButtonClick(issue.issue_name)}
        >
          なぜなぜ分析
        </Button>
      </Box>
    );
  };

  const handleCauseButtonClick = (keyword?: string) => {
    const url = keyword ? `/cause?keyword=${encodeURIComponent(keyword)}` : "/cause";
    const newWindow = window.open(url, "_blank");
    if (newWindow) {
      newWindow.onload = () => {
        newWindow.document.title = 'なぜなぜ分析/' + (keyword || "");
      };
    }
  };

  return (
    <React.Fragment>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' }, backgroundColor: '#e6f2ff', display: 'flex' }}>
        <TableCell style={{ flex: 1, width: '50px', whiteSpace: 'nowrap' }}>{index + 1}</TableCell>
        <TableCell style={{ flex: 1 }}>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRight />}
          </IconButton>
        </TableCell>
        <TableCell style={{ flex: 6 }} component="th" scope="row">
          {row.cluster_title}
        </TableCell>
        <TableCell style={{ flex: 2 }}>{formatNumber(row.cluster_score)}</TableCell>
        <Button size="small" onClick={() => setShowSimilarCompanyIssues(!showSimilarCompanyIssues)}>
          {showSimilarCompanyIssues ? (
            <>
              <KeyboardArrowDownIcon />
              <span style={{ marginLeft: '5px' }}>似た課題を持つ企業</span>
            </>
          ) : (
            <>
              <KeyboardArrowRight />
              <span style={{ marginLeft: '5px' }}>似た課題を持つ企業</span>
            </>
          )}
        </Button>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0, flex: 12 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <TableContainer sx={{ maxHeight: 440 }}>
                <Table stickyHeader aria-label="sticky table">
                  <IssuesTable
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={handleRequestSort}
                    showSimilarCompanyIssues={showSimilarCompanyIssues}
                  />
                  <TableBody>
                    {sortedIssues.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          一致する課題は見つかりませんでした
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedIssues.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((issues, index) => (
                        <TableRow key={issues.id} style={{ display: 'flex' }}>
                          <TableCell style={{ flex: 0.5 }}>{page * rowsPerPage + index + 1}</TableCell>
                          <TableCell style={{ flex: 6 }} component="th" scope="row">
                            {formatIssueInfo(issues)}
                          </TableCell>
                          <TableCell style={{ flex: 0.7 }}>{formatNumber(issues.issue_score)}</TableCell>
                          <TableCell style={{ flex: 5, display: showSimilarCompanyIssues ? 'table-cell' : 'none' }}>
                            {issues.similar_company_issues.length > 0 ? (
                              issues.similar_company_issues.map((company, idx) => formatCompanyInfo(company)).some(info => info !== null) ? (
                                issues.similar_company_issues.map((company, idx) => (
                                  <Typography key={idx} variant="body2" component="div">
                                    {formatCompanyInfo(company)}
                                  </Typography>
                                ))
                              ) : (
                                <Typography variant="body2" component="div">類似課題を持つ企業が見つかりませんでした</Typography>
                              )
                            ) : (
                              <Typography variant="body2" component="div">類似課題を持つ企業が見つかりませんでした</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={sortedIssues.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

export default ClustersTable;